import { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { navigationStateFrom } from '@/types/navigation';
import {
    Package,
    PackageCheck,
    AlertTriangle,
    UserPlus01,
    Users01,
    HelpCircle,
    FileX02,
    Bell01,
    FilterLines,
    ChevronLeft,
    ChevronRight,
} from '@untitledui/icons';
import { getLocalTimeZone, today, startOfMonth, endOfMonth, parseDate } from '@internationalized/date';
import type { DateValue } from 'react-aria-components';
import { useTranslation } from 'react-i18next';

// RangeValue type for date ranges
type RangeValue<T> = { start: T; end: T } | null;
import { useContainerStatsRecap } from '@/hooks/api/use-containers-recap';
import { useCompanyProfile } from '@/hooks/api/use-company-profile';
import { useCarriers } from '@/hooks/api/use-carriers';
import { Tabs, TabList, Tab, TabPanel } from '@/components/application/tabs/tabs';
import { Tooltip, TooltipTrigger } from '@/components/base/tooltip/tooltip';
import { DateRangePicker } from '@/components/application/date-picker/date-range-picker';
import { Select } from '@/components/base/select/select';
import { Toggle } from '@/components/base/toggle/toggle';
import { Button } from '@/components/base/buttons/button';
import { RiskyShipmentsTable } from '@/components/application/containers/risky-shipments-table';
import { CompanyStatsPanel } from '@/components/application/company/company-stats-panel';
import { AttentionDetail } from '@/components/application/control-hub/attention-detail';
import { IssuesOverview } from '@/components/application/control-hub/issues-overview';
import type { ContainerStats } from '@/types/container';
import type { ShipmentStatusFilter } from '@/types/api/containers-recap';
import { SHIPMENT_STATUS_OPTIONS } from '@/types/api/containers-recap';

// ============================================================================
// Local Components
// ============================================================================

interface StatCardProps {
    title: string;
    value: number;
    icon: React.ComponentType<{ className?: string }>;
    color: 'brand' | 'success' | 'warning' | 'error';
    tooltip?: {
        title: string;
        description?: string;
    };
    className?: string;
    onClick?: () => void;
    role?: string;
    tabIndex?: number;
    onKeyDown?: (e: React.KeyboardEvent) => void;
}

const StatCard = ({ title, value, icon: Icon, color, tooltip, className, onClick, role, tabIndex, onKeyDown }: StatCardProps) => {
    const colorClasses = {
        brand: 'text-brand-600 bg-brand-50',
        success: 'text-success-600 bg-success-50',
        warning: 'text-warning-600 bg-warning-50',
        error: 'text-error-600 bg-error-50'
    };

    return (
        <div
            className={`rounded-xl bg-primary p-4 shadow-xs ring-1 ring-secondary ${className ?? ''}`}
            onClick={onClick}
            role={role}
            tabIndex={tabIndex}
            onKeyDown={onKeyDown}
        >
            <div className="flex items-center">
                <div className={`rounded-md p-2 ${colorClasses[color]}`}>
                    <Icon className="size-5" />
                </div>
                <div className="ml-3 flex-1 min-w-0">
                    <div className="flex items-center gap-1">
                        <p className="text-xs font-medium text-tertiary truncate">{title}</p>
                        {tooltip && (
                            <Tooltip
                                title={tooltip.title}
                                description={tooltip.description}
                                placement="top"
                                arrow
                            >
                                <TooltipTrigger>
                                    <HelpCircle className="size-3 shrink-0 text-quaternary hover:text-tertiary cursor-help" />
                                </TooltipTrigger>
                            </Tooltip>
                        )}
                    </div>
                    <p className="text-xl font-semibold text-primary tabular-nums">{value}</p>
                </div>
            </div>
        </div>
    );
};

interface ClickableStatCardProps extends StatCardProps {
    href?: string;
    /** Optional className for the clickable wrapper (e.g. min-w + w-max so carousel slot matches card width) */
    className?: string;
}

const ClickableStatCard = ({ href, className: wrapperClassName, ...props }: ClickableStatCardProps) => {
    const location = useLocation();
    const navigate = useNavigate();

    if (!href) {
        return <StatCard {...props} className={wrapperClassName} />;
    }

    return (
        <StatCard
            {...props}
            className={`cursor-pointer transition-all hover:ring-2 hover:ring-brand-200 ${wrapperClassName ?? ''}`}
            onClick={() => navigate(href, { state: navigationStateFrom(location) })}
            role="link"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && navigate(href, { state: navigationStateFrom(location) })}
        />
    );
};

interface CompactStatBadgeProps {
    label: string;
    value: number;
    icon: React.ComponentType<{ className?: string }>;
    href?: string;
}

const CompactStatBadge = ({ label, value, icon: Icon, href }: CompactStatBadgeProps) => {
    const location = useLocation();
    const navigate = useNavigate();

    const content = (
        <div className="flex items-center gap-2 rounded-lg bg-secondary px-3 py-2 ring-1 ring-secondary_alt">
            <Icon className="size-4 text-tertiary" />
            <span className="text-sm font-medium text-primary">{value}</span>
            <span className="text-sm text-tertiary">{label}</span>
        </div>
    );

    if (!href) {
        return content;
    }

    return (
        <div
            onClick={() => navigate(href, { state: navigationStateFrom(location) })}
            className="cursor-pointer transition-all hover:ring-2 hover:ring-brand-200 rounded-lg"
            role="link"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && navigate(href, { state: navigationStateFrom(location) })}
        >
            {content}
        </div>
    );
};


// ============================================================================
// Helpers
// ============================================================================

interface TabItem {
    id: string;
    label: string;
}

const getDateRangeStrings = (range: RangeValue<DateValue> | null): { startDate: string | null; endDate: string | null } => {
    if (!range || !range.start || !range.end) {
        return { startDate: null, endDate: null };
    }
    const formatDate = (d: DateValue) => {
        return `${d.year}-${String(d.month).padStart(2, '0')}-${String(d.day).padStart(2, '0')}`;
    };
    return {
        startDate: formatDate(range.start),
        endDate: formatDate(range.end),
    };
};

// ============================================================================
// Main Page Component
// ============================================================================

export const ShipperControlHubPage = () => {
    const { t } = useTranslation(['pages:dashboard', 'pages:containers', 'common']);
    const [searchParams, setSearchParams] = useSearchParams();

    // Default date range (this month)
    const now = today(getLocalTimeZone());
    const defaultStart = startOfMonth(now);
    const defaultEnd = endOfMonth(now);

    // Initialize state from URL params
    const getInitialDateRange = (): RangeValue<DateValue> => {
        const startParam = searchParams.get('start_date');
        const endParam = searchParams.get('end_date');
        if (startParam && endParam) {
            try {
                return { start: parseDate(startParam), end: parseDate(endParam) };
            } catch { /* fall through to default */ }
        }
        return { start: defaultStart, end: defaultEnd };
    };

    const [activeTab, setActiveTab] = useState<string>(searchParams.get('tab') || 'container-summary');
    const [dateRange, setDateRange] = useState<RangeValue<DateValue> | null>(getInitialDateRange);
    const [filtersExpanded, setFiltersExpanded] = useState(
        !!(searchParams.get('carrier') || searchParams.get('status') || searchParams.get('include_medium_risk'))
    );
    const [carrierFilter, setCarrierFilter] = useState<string>(searchParams.get('carrier') || '');
    const [statusFilter, setStatusFilter] = useState<ShipmentStatusFilter | ''>(
        (searchParams.get('status') as ShipmentStatusFilter) || ''
    );
    const [includeMediumRisk, setIncludeMediumRisk] = useState(searchParams.get('include_medium_risk') === 'true');

    // Ref to track initialization (prevents sync loop on mount)
    const isInitializing = useRef(true);
    useEffect(() => {
        const timer = setTimeout(() => { isInitializing.current = false; }, 0);
        return () => clearTimeout(timer);
    }, []);

    // Stats carousel (overflow → show arrows, same pattern as Eve Care quick actions)
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);
    const statsCarouselRef = useRef<HTMLDivElement>(null);

    const handleDateRangeChange = (value: RangeValue<DateValue> | null) => {
        setDateRange(value);
    };

    // Convert date range to ISO strings for API calls
    const { startDate, endDate } = useMemo(() => getDateRangeStrings(dateRange), [dateRange]);

    // Carrier options from API
    const { data: carriersData } = useCarriers({ carrier_type: 'ocean' }, true);
    const carrierOptions = useMemo(() => {
        const options = [{ id: '', label: 'All Carriers' }];
        if (carriersData?.carriers) {
            carriersData.carriers.forEach(carrier => {
                options.push({ id: carrier.scac, label: carrier.name });
            });
        }
        return options;
    }, [carriersData]);

    // Shipment status options
    const statusOptions = useMemo(() =>
        SHIPMENT_STATUS_OPTIONS
            .filter(opt => opt.id !== '')
            .map(opt => ({
                id: opt.id,
                label: opt.label,
            })),
        []);

    const hasActiveFilters = carrierFilter !== '' || statusFilter !== '' || includeMediumRisk;

    const clearFilters = () => {
        setCarrierFilter('');
        setStatusFilter('');
        setIncludeMediumRisk(false);
    };

    // Sync filters to URL params (debounced)
    const defaultStartStr = `${defaultStart.year}-${String(defaultStart.month).padStart(2, '0')}-${String(defaultStart.day).padStart(2, '0')}`;
    const defaultEndStr = `${defaultEnd.year}-${String(defaultEnd.month).padStart(2, '0')}-${String(defaultEnd.day).padStart(2, '0')}`;

    const syncFiltersToUrl = useCallback(() => {
        if (isInitializing.current) return;

        const params = new URLSearchParams();

        // Only sync non-default date range
        if (startDate && startDate !== defaultStartStr) params.set('start_date', startDate);
        if (endDate && endDate !== defaultEndStr) params.set('end_date', endDate);
        if (activeTab !== 'container-summary') params.set('tab', activeTab);
        if (carrierFilter) params.set('carrier', carrierFilter);
        if (statusFilter) params.set('status', statusFilter);
        if (includeMediumRisk) params.set('include_medium_risk', 'true');

        setSearchParams(params, { replace: true });
    }, [startDate, endDate, activeTab, carrierFilter, statusFilter, includeMediumRisk, defaultStartStr, defaultEndStr, setSearchParams]);

    useEffect(() => {
        const timeoutId = setTimeout(() => { syncFiltersToUrl(); }, 100);
        return () => clearTimeout(timeoutId);
    }, [syncFiltersToUrl]);

    // API hooks for dynamic data — with filters (Step 5)
    const {
        data: statsData,
        isLoading: statsLoading,
    } = useContainerStatsRecap({
        startDate,
        endDate,
        carrierScac: carrierFilter || undefined,
        shipmentStatus: statusFilter ? [statusFilter] : undefined,
        enabled: true,
    });

    const {
        data: companyProfile,
    } = useCompanyProfile(true);

    // Map API stats to local state format
    const stats: ContainerStats = useMemo(() => {
        if (statsData) {
            // Compute risk count from distribution based on filter
            const riskCount = includeMediumRisk
                ? statsData.risk_distribution
                    .filter(d => ['medium', 'high', 'critical'].includes(d.label.toLowerCase()))
                    .reduce((sum, d) => sum + d.value, 0)
                : statsData.high_risk;

            return {
                total: statsData.total_containers,
                inTransit: statsData.in_transit,
                delivered: statsData.delivered,
                delayed: 0,
                docIssues: statsData.containers_with_doc_issues ?? statsData.doc_issue_count ?? statsData.pending_documents,
                activeAlerts: statsData.total_issue_count ?? statsData.containers_with_issues,
                highRisk: riskCount,
            };
        }
        return {
            total: 0,
            inTransit: 0,
            delivered: 0,
            delayed: 0,
            docIssues: 0,
            activeAlerts: 0,
            highRisk: 0,
        };
    }, [statsData, includeMediumRisk]);

    // Get team members count from company profile
    const teamMembersCount = companyProfile?.active_team_members ?? 0;
    const activePartnersCount = companyProfile?.active_partners ?? 0;

    const tabItems: TabItem[] = [
        {
            id: 'container-summary',
            label: t('controlHub.tabs.containerSummary', { defaultValue: 'Container Summary' }),
        },
        {
            id: 'company-stats',
            label: t('controlHub.tabs.companyStats', { defaultValue: 'Company Stats' }),
        },
    ];

    // Build URL with date params for navigation (must be before early return so hooks below run every render)
    const buildContainersUrl = (params: Record<string, string>) => {
        const searchParams = new URLSearchParams();
        if (startDate) searchParams.set('start_date', startDate);
        if (endDate) searchParams.set('end_date', endDate);
        Object.entries(params).forEach(([key, value]) => {
            searchParams.set(key, value);
        });
        return `/shipper/containers?${searchParams.toString()}`;
    };

    // Stats carousel scroll (same pattern as Eve Care quick actions) — hooks must run before any conditional return
    const checkStatsCarouselScroll = useCallback(() => {
        const container = statsCarouselRef.current;
        if (!container) return;
        const { scrollLeft, scrollWidth, clientWidth } = container;
        setCanScrollLeft(scrollLeft > 0);
        setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }, []);

    const scrollStatsCarousel = (direction: 'left' | 'right') => {
        const container = statsCarouselRef.current;
        if (!container) return;
        const scrollAmount = 280;
        const newScrollLeft =
            direction === 'left'
                ? container.scrollLeft - scrollAmount
                : container.scrollLeft + scrollAmount;
        container.scrollTo({ left: newScrollLeft, behavior: 'smooth' });
    };

    useEffect(() => {
        const container = statsCarouselRef.current;
        if (!container) return;
        const timeoutId = setTimeout(checkStatsCarouselScroll, 100);
        container.addEventListener('scroll', checkStatsCarouselScroll);
        const resizeObserver = new ResizeObserver(checkStatsCarouselScroll);
        resizeObserver.observe(container);
        return () => {
            clearTimeout(timeoutId);
            container.removeEventListener('scroll', checkStatsCarouselScroll);
            resizeObserver.disconnect();
        };
    }, [checkStatsCarouselScroll]);

    if (statsLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <p className="text-lg text-tertiary">{t('shipper.loading')}</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header with Time Range Selector and Static Counts */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-semibold text-primary">{t('controlHub.title', { ns: 'common' })}</h1>
                    <p className="text-tertiary mt-1">
                        {t('shipper.subtitle', { ns: 'pages:dashboard' })}
                    </p>
                </div>

                <div className="flex items-center gap-3">
                    <CompactStatBadge
                        label={t('controlHub.stats.activePartners', { defaultValue: 'Partners' })}
                        value={activePartnersCount}
                        icon={Users01}
                        href="/shipper/business-partners"
                    />
                    <CompactStatBadge
                        label={t('controlHub.stats.teamMembers', { defaultValue: 'Team' })}
                        value={teamMembersCount}
                        icon={UserPlus01}
                        href="/shipper/team"
                    />

                    <DateRangePicker
                        value={dateRange}
                        onChange={handleDateRangeChange}
                    />
                </div>
            </div>

            {/* Step 5: Filter Bar */}
            <div className="flex items-center gap-3 flex-wrap">
                <Button
                    color="tertiary"
                    size="sm"
                    onClick={() => setFiltersExpanded(!filtersExpanded)}
                    iconLeading={<FilterLines className="size-4" />}
                >
                    <span>Filters</span>
                    {hasActiveFilters && (
                        <span className="ml-1 inline-flex items-center justify-center size-5 rounded-full bg-brand-50 text-brand-700 text-xs font-medium">
                            {(carrierFilter ? 1 : 0) + (statusFilter ? 1 : 0) + (includeMediumRisk ? 1 : 0)}
                        </span>
                    )}
                </Button>
                {filtersExpanded && (
                    <div className="flex flex-wrap items-center gap-3">
                        <div className="w-48">
                            <Select
                                placeholder="Carrier"
                                items={carrierOptions}
                                selectedKey={carrierFilter}
                                onSelectionChange={(key) => setCarrierFilter(key as string)}
                                size="sm"
                            >
                                {(item) => (
                                    <Select.Item key={item.id} id={item.id}>
                                        {item.label}
                                    </Select.Item>
                                )}
                            </Select>
                        </div>
                        <div className="w-48">
                            <Select
                                placeholder="Status"
                                items={[{ id: '', label: 'All Statuses' }, ...statusOptions]}
                                selectedKey={statusFilter}
                                onSelectionChange={(key) => setStatusFilter(key as ShipmentStatusFilter | '')}
                                size="sm"
                            >
                                {(item) => (
                                    <Select.Item key={item.id} id={item.id}>
                                        {item.label}
                                    </Select.Item>
                                )}
                            </Select>
                        </div>
                        <Toggle
                            label="Include Medium Risk"
                            size="sm"
                            isSelected={includeMediumRisk}
                            onChange={setIncludeMediumRisk}
                        />
                        {hasActiveFilters && (
                            <Button
                                color="tertiary"
                                size="sm"
                                onClick={clearFilters}
                            >
                                Clear
                            </Button>
                        )}
                    </div>
                )}
            </div>

            {/* Tabs */}
            <Tabs
                selectedKey={activeTab}
                onSelectionChange={(key) => setActiveTab(key as string)}
            >
                <TabList
                    type="underline"
                    size="md"
                    items={tabItems}
                >
                    {(item) => <Tab key={item.id} id={item.id}>{item.label}</Tab>}
                </TabList>

                {/* Container Summary Tab */}
                <TabPanel id="container-summary" className="mt-6 space-y-6">
                    {/* Stats Cards — carousel with arrows when overflow (same pattern as Eve Care quick actions) */}
                    <div className="flex items-center gap-4">
                        {canScrollLeft && (
                            <button
                                type="button"
                                onClick={() => scrollStatsCarousel('left')}
                                className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-primary border border-secondary shadow-lg hover:bg-secondary transition-colors cursor-pointer"
                                aria-label={t('controlHub.scrollLeft', { defaultValue: 'Scroll left' })}
                            >
                                <ChevronLeft className="size-4 text-primary" />
                            </button>
                        )}
                        <div
                            ref={statsCarouselRef}
                            className="flex-1 flex gap-4 overflow-x-auto scroll-smooth min-w-0 scrollbar-hide"
                            style={{
                                scrollbarWidth: 'none',
                                msOverflowStyle: 'none',
                            }}
                        >
                            <style>{`
                                .scrollbar-hide::-webkit-scrollbar { display: none; }
                            `}</style>
                            <div className="flex-1 min-w-[140px]">
                                <ClickableStatCard
                                    className="min-w-0 w-full"
                                    title={t('shipper.stats.totalContainers')}
                                    value={stats.total}
                                    icon={Package}
                                    color="brand"
                                    href={buildContainersUrl({})}
                                />
                            </div>
                            <div className="flex-1 min-w-[140px]">
                                <ClickableStatCard
                                    className="min-w-0 w-full"
                                    title={t('shipper.stats.inTransit')}
                                    value={stats.inTransit}
                                    icon={PackageCheck}
                                    color="success"
                                    href={buildContainersUrl({ status: 'in_transit' })}
                                />
                            </div>
                            <div className="flex-1 min-w-[140px]">
                                <ClickableStatCard
                                    className="min-w-0 w-full"
                                    title={t('shipper.stats.delivered')}
                                    value={stats.delivered}
                                    icon={PackageCheck}
                                    color="success"
                                    href={buildContainersUrl({ status: 'delivered' })}
                                />
                            </div>
                            <div className="flex-1 min-w-[140px]">
                                <ClickableStatCard
                                    className="min-w-0 w-full"
                                    title={t('shipper.stats.containersWithDocIssues', { defaultValue: 'With Doc Issues' })}
                                    value={stats.docIssues}
                                    icon={FileX02}
                                    color="warning"
                                    tooltip={{
                                        title: t('controlHub.tooltips.docIssues.title', { defaultValue: 'Containers with Document Issues' }),
                                        description: t('controlHub.tooltips.docIssues.description', { defaultValue: 'Number of containers that have at least one active document issue (missing docs, check failures, cross-check errors)' }),
                                    }}
                                    href={buildContainersUrl({ has_doc_issues: 'true' })}
                                />
                            </div>
                            <div className="flex-1 min-w-[140px]">
                                <ClickableStatCard
                                    className="min-w-0 w-full"
                                    title={t('shipper.stats.issues', { defaultValue: 'With Issues' })}
                                    value={stats.activeAlerts}
                                    icon={Bell01}
                                    color="warning"
                                    tooltip={{
                                        title: t('controlHub.tooltips.issues.title', { defaultValue: 'Issues' }),
                                        description: t('controlHub.tooltips.issues.description', { defaultValue: 'Total active issues across all containers' }),
                                    }}
                                    href={buildContainersUrl({ has_active_alerts: 'true' })}
                                />
                            </div>
                            <div className="flex-1 min-w-[140px]">
                                <ClickableStatCard
                                    className="min-w-0 w-full"
                                    title={includeMediumRisk
                                        ? t('shipper.stats.atRisk', { defaultValue: 'At Risk' })
                                        : t('shipper.stats.highRisk')
                                    }
                                    value={stats.highRisk}
                                    icon={AlertTriangle}
                                    color="error"
                                    tooltip={{
                                        title: includeMediumRisk
                                            ? t('controlHub.tooltips.atRisk.title', { defaultValue: 'At Risk Shipments' })
                                            : t('controlHub.tooltips.highRisk.title', { defaultValue: 'High Risk Shipments' }),
                                        description: includeMediumRisk
                                            ? t('controlHub.tooltips.atRisk.description', { defaultValue: 'Containers with risk score ≥ 25% (medium, high, critical)' })
                                            : t('controlHub.tooltips.highRisk.description', { defaultValue: 'Containers with risk score ≥ 50% requiring attention' }),
                                    }}
                                    href={buildContainersUrl({ risk: includeMediumRisk ? 'medium,high,critical' : 'high,critical' })}
                                />
                            </div>
                        </div>
                        {canScrollRight && (
                            <button
                                type="button"
                                onClick={() => scrollStatsCarousel('right')}
                                className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-primary border border-secondary shadow-lg hover:bg-secondary transition-colors cursor-pointer"
                                aria-label={t('controlHub.scrollRight', { defaultValue: 'Scroll right' })}
                            >
                                <ChevronRight className="size-4 text-primary" />
                            </button>
                        )}
                    </div>

                    {/* Risky Shipments Table */}
                    <RiskyShipmentsTable startDate={startDate} endDate={endDate} maxCount={10} includeMedium={includeMediumRisk} />

                    {/* Issues Overview + Requires Attention — side by side */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <IssuesOverview startDate={startDate} endDate={endDate} />
                        <AttentionDetail startDate={startDate} endDate={endDate} />
                    </div>
                </TabPanel>

                {/* Company Stats Tab */}
                <TabPanel id="company-stats" className="mt-6">
                    <CompanyStatsPanel />
                </TabPanel>
            </Tabs>
        </div>
    );
};
